import React, { useState, useEffect, useCallback } from "react";
import { Landmark, RefreshCw, Lock, RotateCcw, CheckCircle2, AlertTriangle, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Card, Spinner, PageHeader, Badge, Button, Modal, Input, Select } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, PeriodPicker, BranchFilter } from "../../components/finance";
import { JournalTypeBadge, JournalLinesTable, SOURCE_LABEL } from "../../components/JournalPreview";

const TABS = [
  { id: "balance-sheet", label: "Neraca" },
  { id: "pnl", label: "Laba Rugi" },
  { id: "trial-balance", label: "Neraca Saldo" },
  { id: "ledger", label: "Buku Besar" },
  { id: "journals", label: "Jurnal" },
  { id: "coa", label: "Bagan Akun" },
  { id: "reconciliation", label: "Rekonsiliasi Bank" },
  { id: "closing", label: "Tutup Buku" },
  { id: "integration", label: "Status Integrasi" },
];

export default function Accounting() {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_accounting");
  const [tab, setTab] = useState("balance-sheet");
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [rebuilding, setRebuilding] = useState(false);

  const rebuild = async () => {
    setRebuilding(true);
    try { const { data } = await api.post("/accounting/rebuild"); toast.success("Jurnal disinkron ulang dari data operasional"); console.log(data.counts); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setRebuilding(false); }
  };

  return (
    <div data-testid="accounting-page">
      <PageHeader title="Accounting" subtitle="Jurnal otomatis dari seluruh operasional — Buku Besar, Neraca Saldo & Neraca" icon={Landmark}>
        <div className="flex flex-wrap items-center gap-2">
          <BranchFilter value={branchId} onChange={setBranchId} testid="acc-branch-filter" />
          <PeriodPicker value={period} onChange={setPeriod} testid="acc-period" />
          {canManage && <Button variant="outline" onClick={rebuild} loading={rebuilding} data-testid="acc-rebuild-btn"><RefreshCw size={15} /> Resync</Button>}
        </div>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="acc-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`acc-tab-${t.id}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-primary text-white" : "bg-stone-100 text-ink-soft hover:bg-stone-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "balance-sheet" && <BalanceSheet period={period} branchId={branchId} toast={toast} />}
      {tab === "pnl" && <PnL period={period} branchId={branchId} toast={toast} />}
      {tab === "trial-balance" && <TrialBalance period={period} branchId={branchId} toast={toast} />}
      {tab === "ledger" && <Ledger period={period} branchId={branchId} toast={toast} />}
      {tab === "journals" && <Journals period={period} branchId={branchId} canManage={canManage} toast={toast} />}
      {tab === "coa" && <COA toast={toast} />}
      {tab === "reconciliation" && <Reconciliation period={period} toast={toast} />}
      {tab === "closing" && <Closing canManage={canManage} toast={toast} />}
      {tab === "integration" && <Integration toast={toast} />}
    </div>
  );
}

function useFetch(url, params, deps) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(url, { params }); setD(data); }
    catch { setD(null); } finally { setLoading(false); }
  }, deps); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  return { d, loading, reload: load };
}

const Loader = () => <div className="flex h-48 items-center justify-center"><Spinner className="h-8 w-8" /></div>;
const Section = ({ title, lines, total, totalLabel }) => (
  <div>
    <h4 className="mb-1 font-heading font-bold text-ink">{title}</h4>
    {lines.map((l) => (
      <div key={l.account_code} className="flex justify-between py-1 text-sm">
        <span className="text-ink-soft">{l.account_code !== "LABA" ? `${l.account_code} · ` : ""}{l.account_name}</span>
        <span className="tabular-nums text-ink">{rp(l.amount)}</span>
      </div>
    ))}
    <div className="mt-1 flex justify-between border-t border-line py-1.5 text-sm font-bold">
      <span>{totalLabel || `Total ${title}`}</span><span className="tabular-nums">{rp(total)}</span>
    </div>
  </div>
);

function BalanceSheet({ period, branchId }) {
  const { d, loading } = useFetch("/accounting/balance-sheet", { period, branch_id: branchId || undefined }, [period, branchId]);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div data-testid="acc-balance-sheet">
      <div className={`mb-3 flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${d.balanced ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="acc-bs-identity">
        {d.balanced ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        Aset {rp(d.total_assets)} = Kewajiban + Ekuitas {rp(d.liabilities_plus_equity)} {d.balanced ? "(Seimbang)" : "(TIDAK SEIMBANG)"}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card><Section title="Aset" lines={d.assets} total={d.total_assets} totalLabel="Total Aset" /></Card>
        <div className="space-y-4">
          <Card><Section title="Kewajiban" lines={d.liabilities} total={d.total_liabilities} totalLabel="Total Kewajiban" /></Card>
          <Card><Section title="Ekuitas" lines={d.equity} total={d.total_equity} totalLabel="Total Ekuitas" /></Card>
        </div>
      </div>
    </div>
  );
}

function PnL({ period, branchId }) {
  const { d, loading } = useFetch("/accounting/pnl", { period, branch_id: branchId || undefined }, [period, branchId]);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="acc-pnl">
      <Card><Section title="Pendapatan" lines={d.revenue_lines} total={d.total_revenue} totalLabel="Total Pendapatan" /></Card>
      <Card>
        <Section title="Beban" lines={d.expense_lines} total={d.total_expense} totalLabel="Total Beban" />
        <div className="mt-3 flex justify-between rounded-xl bg-stone-50 px-3 py-2 font-bold text-ink" data-testid="acc-pnl-net">
          <span>Laba Bersih</span><span className={d.net_income >= 0 ? "text-emerald-600" : "text-red-600"}>{rp(d.net_income)}</span>
        </div>
      </Card>
    </div>
  );
}

function TrialBalance({ period, branchId }) {
  const { d, loading } = useFetch("/accounting/trial-balance", { period, branch_id: branchId || undefined }, [period, branchId]);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <Card data-testid="acc-trial-balance">
      <div className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${d.balanced ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="acc-tb-status">
        {d.balanced ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {d.balanced ? "Seimbang" : "Tidak Seimbang"}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <tr><th className="py-2 pr-3">Kode</th><th className="py-2 pr-3">Akun</th><th className="py-2 pr-3 text-right">Debit</th><th className="py-2 pr-3 text-right">Kredit</th></tr>
          </thead>
          <tbody className="divide-y divide-line">
            {d.rows.map((r) => (
              <tr key={r.account_code}>
                <td className="py-2 pr-3 text-ink-mute">{r.account_code}</td>
                <td className="py-2 pr-3 text-ink">{r.account_name}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.debit ? rp(r.debit) : "—"}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.credit ? rp(r.credit) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink font-bold">
              <td colSpan={2} className="py-2">TOTAL</td>
              <td className="py-2 text-right tabular-nums">{rp(d.total_debit)}</td>
              <td className="py-2 text-right tabular-nums">{rp(d.total_credit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

function Ledger({ period, branchId }) {
  const { d, loading } = useFetch("/accounting/general-ledger", { period, branch_id: branchId || undefined }, [period, branchId]);
  const [open, setOpen] = useState(null);
  const [traceId, setTraceId] = useState(null);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div className="space-y-2" data-testid="acc-ledger">
      {d.accounts.length === 0 && <p className="py-6 text-center text-sm text-ink-mute">Tidak ada transaksi pada periode ini.</p>}
      {d.accounts.map((a) => (
        <Card key={a.account_code} className="!p-0 overflow-hidden" data-testid={`acc-ledger-${a.account_code}`}>
          <button onClick={() => setOpen(open === a.account_code ? null : a.account_code)} className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-stone-50">
            <span className="flex items-center gap-2"><ChevronRight size={15} className={`transition-transform ${open === a.account_code ? "rotate-90" : ""}`} /><span className="font-semibold text-ink">{a.account_code} · {a.account_name}</span><Badge tone="mute">{a.entries.length}</Badge></span>
            <span className="tabular-nums font-bold text-ink">{rp(a.closing_balance)}</span>
          </button>
          {open === a.account_code && (
            <div className="overflow-x-auto border-t border-line">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase text-ink-mute"><tr><th className="py-2 px-3">Tanggal</th><th className="py-2 px-3">Keterangan</th><th className="py-2 px-3">Sumber</th><th className="py-2 px-3 text-right">Debit</th><th className="py-2 px-3 text-right">Kredit</th><th className="py-2 px-3 text-right">Saldo</th></tr></thead>
                <tbody className="divide-y divide-line">
                  {a.entries.map((e, i) => (
                    <tr key={i} onClick={() => e.journal_id && setTraceId(e.journal_id)} className="cursor-pointer hover:bg-primary/5" title="Klik untuk drill-down ke jurnal & transaksi sumber" data-testid={`acc-gl-entry-${a.account_code}-${i}`}>
                      <td className="py-1.5 px-3 text-ink-soft">{e.date}</td>
                      <td className="py-1.5 px-3 text-ink">{e.description}</td>
                      <td className="py-1.5 px-3"><Badge tone="info" className="!text-[10px]">{SOURCE_LABEL[e.source_module] || e.source_module}</Badge></td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{e.debit ? rp(e.debit) : "—"}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{e.credit ? rp(e.credit) : "—"}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums font-medium">{rp(e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}
      {traceId && <JournalDetailModal journalId={traceId} onClose={() => setTraceId(null)} />}
    </div>
  );
}

const NODE_ICON = { pos_entry: "🧾", daily_closing: "🔒", sale_line: "🛒", purchase: "📦", supplier: "🏭", stock_movement: "🔄", purchase_order: "📋", order: "🚚", stock_transfer: "↔️", opname: "📊", payroll: "💰", production: "🏭", waste: "🗑️", incident: "⚠️", opex: "💸", petty_cash: "💵", petty_topup: "💳", finance: "🏦", kasbon: "🤝", journal: "📒" };

function JournalDetailModal({ journalId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/accounting/journals/${journalId}/trace`).then(({ data }) => setData(data)).catch(() => setData({ journal: null, chain: [] }));
  }, [journalId]);
  const j = data?.journal;
  return (
    <Modal open onClose={onClose} title="Detail Jurnal & Sumber Transaksi" size="lg">
      {!data ? <Loader /> : !j ? <p className="py-6 text-center text-sm text-ink-mute">Jurnal tidak ditemukan.</p> : (
        <div className="space-y-4" data-testid="acc-journal-detail">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-heading font-bold text-ink">{j.description}</p>
              <JournalTypeBadge journal={j} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-mute">
              <span>{j.date}</span>
              <span>Modul: <b className="text-ink-soft">{SOURCE_LABEL[j.source_module] || j.source_module}</b></span>
              {j.branch_name && <span>Cabang: <b className="text-ink-soft">{j.branch_name}</b></span>}
              {j.source_doc_number && <span>No. Ref: <b className="font-mono text-ink-soft" data-testid="acc-journal-ref">{j.source_doc_number}</b></span>}
              {j.created_by && <span>Oleh: <b className="text-ink-soft">{j.created_by}</b></span>}
            </div>
          </div>
          <JournalLinesTable journal={j} />
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">Lacak Sumber (Drill-down)</p>
            {data.chain.length === 0 ? (
              <p className="text-xs text-ink-mute" data-testid="acc-trace-empty">{["manual", "opening", "closing"].includes(j.source_module) ? "Jurnal ini dibuat langsung di Accounting (tanpa dokumen operasional)." : "Dokumen sumber tidak ditemukan."}</p>
            ) : (
              <div className="space-y-2" data-testid="acc-trace-chain">
                {data.chain.map((n, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="flex flex-col items-center">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm">{NODE_ICON[n.type] || "📄"}</span>
                      {i < data.chain.length - 1 && <span className="mt-1 h-4 w-px bg-line" />}
                    </div>
                    <div className="min-w-0 flex-1 rounded-xl border border-line bg-app px-3 py-2" data-testid={`acc-trace-node-${n.type}`}>
                      <div className="flex flex-wrap items-center justify-between gap-x-3">
                        <p className="text-sm font-semibold text-ink">{n.title}</p>
                        {n.amount != null && <span className="tabular-nums text-sm font-bold text-ink">{rp(n.amount)}</span>}
                      </div>
                      <p className="text-[11px] text-ink-mute">
                        {n.doc_number && <span className="mr-2 font-mono font-semibold text-primary">{n.doc_number}</span>}
                        {n.date && <span className="mr-2">{n.date}</span>}
                        {n.branch_name && <span className="mr-2">{n.branch_name}</span>}
                        {n.detail && <span>{n.detail}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

const SRC_TONE = { sales: "success", purchases: "warn", payroll: "info", finance: "mute", manual: "danger", opening: "mute", reversal: "danger" };

function Journals({ period, branchId, canManage, toast }) {
  const { d, loading, reload } = useFetch("/accounting/journals", { period, branch_id: branchId || undefined, limit: 300 }, [period, branchId]);
  const [showManual, setShowManual] = useState(false);
  const [traceId, setTraceId] = useState(null);
  const reverse = async (id) => {
    if (!window.confirm("Buat jurnal pembalik?")) return;
    try { await api.post(`/accounting/journals/${id}/reverse`); toast.success("Jurnal pembalik dibuat"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="acc-journals">
      {canManage && <div className="mb-3"><Button variant="outline" onClick={() => setShowManual(true)} data-testid="acc-manual-journal-btn"><Plus size={15} /> Jurnal Manual</Button></div>}
      <div className="space-y-2">
        {(d?.journals || []).map((j) => (
          <Card key={j.id} className="!py-3 cursor-pointer transition-colors hover:border-primary/40" data-testid={`acc-journal-${j.id}`} onClick={() => setTraceId(j.id)}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="font-semibold text-ink">{j.description}</p>
                  <JournalTypeBadge journal={j} />
                </div>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-mute">
                  <span>{j.date}</span>
                  <Badge tone={SRC_TONE[j.source_module] || "mute"} className="!text-[10px]">{SOURCE_LABEL[j.source_module] || j.source_module}</Badge>
                  {j.source_doc_number && <span className="font-mono text-[10px] font-semibold text-primary" data-testid={`acc-journal-ref-${j.id}`}>{j.source_doc_number}</span>}
                  {j.branch_name && <span>{j.branch_name}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="tabular-nums font-bold text-ink">{rp(j.total)}</span>
                {canManage && !j.reversed_by && j.source_module !== "reversal" && <button onClick={(e) => { e.stopPropagation(); reverse(j.id); }} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" title="Balik jurnal" data-testid={`acc-reverse-${j.id}`}><RotateCcw size={14} /></button>}
              </div>
            </div>
            <div className="mt-2 space-y-0.5">
              {j.lines.map((l, i) => (
                <div key={i} className={`flex justify-between text-xs ${l.debit ? "" : "pl-6"}`}>
                  <span className="text-ink-soft">{l.account_code} · {l.account_name}</span>
                  <span className="tabular-nums">{l.debit ? rp(l.debit) : rp(l.credit)}</span>
                </div>
              ))}
            </div>
          </Card>
        ))}
        {(!d?.journals || d.journals.length === 0) && <p className="py-6 text-center text-sm text-ink-mute">Belum ada jurnal.</p>}
      </div>
      <ManualJournalModal open={showManual} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); reload(); }} toast={toast} />
      {traceId && <JournalDetailModal journalId={traceId} onClose={() => setTraceId(null)} />}
    </div>
  );
}

function ManualJournalModal({ open, onClose, onDone, toast }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [lines, setLines] = useState([{ account_code: "", debit: 0, credit: 0 }, { account_code: "", debit: 0, credit: 0 }]);
  const [isAdj, setIsAdj] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/accounting/coa").then(({ data }) => setAccounts(data.accounts)).catch(() => {}); }, [open]);
  const totD = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totC = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = Math.abs(totD - totC) < 0.01 && totD > 0;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/accounting/journals", { date, description: desc, is_adjustment: isAdj, lines: lines.filter((l) => l.account_code && (Number(l.debit) || Number(l.credit))).map((l) => ({ account_code: l.account_code, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })) });
      toast.success("Jurnal tersimpan"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Jurnal Manual" size="lg"
      footer={<><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} loading={saving} disabled={!balanced || !desc} data-testid="acc-manual-submit">Simpan</Button></>}>
      <div className="space-y-3" data-testid="acc-manual-form">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Tanggal" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="acc-manual-date" />
          <Input label="Keterangan" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="acc-manual-desc" />
        </div>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 items-end gap-2">
            <div className="col-span-6"><Select label={i === 0 ? "Akun" : ""} value={l.account_code} onChange={(e) => setLines(lines.map((x, idx) => idx === i ? { ...x, account_code: e.target.value } : x))} data-testid={`acc-manual-acct-${i}`}><option value="">— pilih akun —</option>{accounts.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}</Select></div>
            <div className="col-span-3"><Input label={i === 0 ? "Debit" : ""} type="number" value={l.debit} onChange={(e) => setLines(lines.map((x, idx) => idx === i ? { ...x, debit: e.target.value, credit: 0 } : x))} data-testid={`acc-manual-debit-${i}`} /></div>
            <div className="col-span-3"><Input label={i === 0 ? "Kredit" : ""} type="number" value={l.credit} onChange={(e) => setLines(lines.map((x, idx) => idx === i ? { ...x, credit: e.target.value, debit: 0 } : x))} data-testid={`acc-manual-credit-${i}`} /></div>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setLines([...lines, { account_code: "", debit: 0, credit: 0 }])}><Plus size={14} /> Baris</Button>
          <span className={`text-sm font-bold ${balanced ? "text-emerald-600" : "text-red-600"}`} data-testid="acc-manual-balance">Debit {rp(totD)} / Kredit {rp(totC)} {balanced ? "✓" : "✗"}</span>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={isAdj} onChange={(e) => setIsAdj(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="acc-manual-adjustment" />
          Tandai sebagai <b>Jurnal Penyesuaian</b> 🟡
        </label>
      </div>
    </Modal>
  );
}

function COA() {
  const { d, loading } = useFetch("/accounting/coa", {}, []);
  if (loading) return <Loader />;
  const groups = { ASSET: "Aset", LIABILITY: "Kewajiban", EQUITY: "Ekuitas", REVENUE: "Pendapatan", EXPENSE: "Beban" };
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="acc-coa">
      {Object.entries(groups).map(([type, label]) => (
        <Card key={type}>
          <h4 className="mb-2 font-heading font-bold text-ink">{label}</h4>
          {(d?.accounts || []).filter((a) => a.type === type).map((a) => (
            <div key={a.code} className="flex justify-between py-1 text-sm"><span className="text-ink-soft">{a.code} · {a.name}</span><Badge tone="mute" className="!text-[10px]">{a.normal_balance === "debit" ? "D" : "K"}</Badge></div>
          ))}
        </Card>
      ))}
    </div>
  );
}

function Reconciliation({ period }) {
  const { d, loading } = useFetch("/accounting/bank-reconciliation", { period }, [period]);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div data-testid="acc-reconciliation">
      <div className="mb-3 grid gap-3 sm:grid-cols-3">
        <Card><p className="text-xs text-ink-mute">Pergerakan Kas (Buku Besar)</p><p className="font-heading text-lg font-bold text-ink">{rp(d.ledger_cash_movement)}</p></Card>
        <Card><p className="text-xs text-ink-mute">Mutasi Bank (Impor)</p><p className="font-heading text-lg font-bold text-ink">{rp(d.bank_net_movement)}</p></Card>
        <Card><p className="text-xs text-ink-mute">Cocok / Belum</p><p className="font-heading text-lg font-bold text-ink">{d.matched_count} / {d.unmatched_count}</p></Card>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs uppercase text-ink-mute"><tr><th className="py-2 pr-3">Tanggal</th><th className="py-2 pr-3">Keterangan</th><th className="py-2 pr-3 text-right">Jumlah</th><th className="py-2 pr-3 text-center">Terjurnal</th></tr></thead>
          <tbody className="divide-y divide-line">
            {d.entries.map((e) => (
              <tr key={e.id}><td className="py-2 pr-3 text-ink-soft">{e.date}</td><td className="py-2 pr-3 text-ink">{e.note}</td><td className="py-2 pr-3 text-right tabular-nums">{rp(e.amount)}</td><td className="py-2 pr-3 text-center">{e.journaled ? <Badge tone="success">Ya</Badge> : <Badge tone="warn">Belum</Badge>}</td></tr>
            ))}
            {d.entries.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-ink-mute">Tidak ada mutasi bank pada periode ini.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Closing({ canManage, toast }) {
  const { d, loading, reload } = useFetch("/accounting/closings", {}, []);
  const [period, setPeriod] = useState(() => { const dt = new Date(); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`; });
  const [busy, setBusy] = useState(false);
  const close = async () => {
    if (!window.confirm(`Tutup buku periode ${period}? Tindakan ini mengunci periode.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/accounting/close-period?period=${period}`); toast.success(`Periode ditutup. Laba bersih ${rp(data.net_income)} → Laba Ditahan`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="acc-closing">
      {canManage && (
        <Card className="mb-3">
          <h4 className="mb-2 font-heading font-bold text-ink">Tutup Buku Periode</h4>
          <div className="flex flex-wrap items-end gap-2">
            <PeriodPicker value={period} onChange={setPeriod} testid="acc-closing-period" />
            <Button onClick={close} loading={busy} data-testid="acc-close-btn"><Lock size={15} /> Tutup Buku</Button>
          </div>
          <p className="mt-2 text-xs text-ink-mute">Memindahkan saldo Pendapatan & Beban ke Ikhtisar Laba Rugi (3300), lalu ke Laba Ditahan (3100).</p>
        </Card>
      )}
      <Card>
        <h4 className="mb-2 font-heading font-bold text-ink">Riwayat Penutupan</h4>
        {(d?.closings || []).length === 0 ? <p className="py-3 text-sm text-ink-mute">Belum ada periode yang ditutup.</p> : (d.closings.map((c) => (
          <div key={c.id} className="flex items-center justify-between border-b border-line py-2 text-sm last:border-0" data-testid={`acc-closing-${c.period}`}>
            <span className="flex items-center gap-2 text-ink"><Lock size={13} className="text-ink-mute" /> {c.period}</span>
            <span className="tabular-nums text-ink-soft">Laba {rp(c.net_income)}</span>
          </div>
        )))}
      </Card>
    </div>
  );
}

function Integration() {
  const { d, loading } = useFetch("/accounting/integration-status", {}, []);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div data-testid="acc-integration">
      <div className={`mb-3 flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${d.trial_balance_balanced ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
        {d.trial_balance_balanced ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} {d.total_journals} jurnal · Neraca Saldo {d.trial_balance_balanced ? "seimbang" : "tidak seimbang"} (Dr {rp(d.trial_balance_total_debit)} = Cr {rp(d.trial_balance_total_credit)})
      </div>
      {d.pending_events > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700" data-testid="acc-pending-events">
          <AlertTriangle size={16} /> {d.pending_events} event jurnal menunggu diproses ulang (akan otomatis diselesaikan oleh sinkronisasi berkala)
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {d.modules.map((m) => (
          <Card key={m.module} data-testid={`acc-int-${m.module}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="font-semibold text-ink">{m.label || m.module}</p><p className="text-xs text-ink-mute">{m.source_records} transaksi sumber</p></div>
              {m.linked ? <Badge tone="success"><CheckCircle2 size={12} /> Terhubung</Badge> : <Badge tone="warn">Belum</Badge>}
            </div>
            <p className="mt-2 text-sm text-ink-soft">{m.journals_generated} jurnal dihasilkan{m.last_journal_date ? ` · terakhir ${m.last_journal_date}` : ""}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
